import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const GlobalNotificationListener = () => {
  const [queriesList, setQueriesList] = useState([]);
  const [notifiedHistory, setNotifiedHistory] = useState({});

  // Function to load data from localStorage
  const loadData = () => {
    const savedQueries = localStorage.getItem('query_page_tickets');
    if (savedQueries) {
      try {
        const parsed = JSON.parse(savedQueries);
        setQueriesList(parsed);
      } catch (e) {
        console.error("Failed to parse stored ticket queries", e);
      }
    }

    const savedHistory = localStorage.getItem('query_page_notified_history');
    if (savedHistory) {
      try {
        setNotifiedHistory(JSON.parse(savedHistory));
      } catch (e) {
        console.error("Failed to parse stored notification history", e);
      }
    }
  };

  // Load data on mount and set up listeners
  useEffect(() => {
    loadData();

    const handleStorageChange = (e) => {
      if (e.key === 'query_page_tickets') {
        loadData();
      }
    };

    const handleQueriesUpdate = () => {
      loadData();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadData();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('queriesUpdated', handleQueriesUpdate);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    const interval = setInterval(loadData, 5000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('queriesUpdated', handleQueriesUpdate);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
    };
  }, []);

  const triggerNotification = (query, status) => {
    toast.info(
      <div className="text-xs font-sans space-y-1">
        <div className="font-bold text-gray-900 border-b pb-1 mb-1">
          📢 Escalation Update ({status})
        </div>
        <p><strong>Ticket ID:</strong> {query.id}</p>
        <p><strong>Company:</strong> {query.companyName}</p>
        {query.subCompanyName && query.subCompanyName !== "N/A" && (
          <p><strong>Sub Company:</strong> {query.subCompanyName}</p>
        )}
        <p><strong>Vehicle(s):</strong> {query.vehicleName}</p>
        <p><strong>Category:</strong> {query.queryType}</p>
        <p className="text-xs text-gray-500 mt-1">Click to view details</p>
      </div>,
      {
        position: "top-right",
        autoClose: 8000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        theme: "light",
      }
    );
  };

  // Check for notifications every minute
  useEffect(() => {
    const checkNotifications = () => {
      const now = new Date();
      let historyChanged = false;
      const updatedHistory = { ...notifiedHistory };

      queriesList.forEach(query => {
        if (query.status !== 'Pending' && query.status !== 'Ongoing') return;

        const createdTime = new Date(query.createdAt.replace(' ', 'T'));
        if (isNaN(createdTime.getTime())) return;

        const diffInMinutes = Math.floor((now - createdTime) / (1000 * 60));
        if (diffInMinutes < 0) return;

        let shouldNotify = false;

        if (diffInMinutes <= 24 * 60) {
          if (diffInMinutes > 0 && diffInMinutes % 30 === 0) {
            shouldNotify = true;
          }
        } else {
          if (diffInMinutes % 5 === 0) {
            shouldNotify = true;
          }
        }

        if (shouldNotify) {
          const notificationKey = `${query.id}-${diffInMinutes}`;
          if (!updatedHistory[notificationKey]) {
            updatedHistory[notificationKey] = true;
            historyChanged = true;
            triggerNotification(query, `Reminder: ${query.status} for ${diffInMinutes} mins`);
          }
        }
      });

      if (historyChanged) {
        setNotifiedHistory(updatedHistory);
        localStorage.setItem('query_page_notified_history', JSON.stringify(updatedHistory));
      }
    };

    checkNotifications();
    const interval = setInterval(checkNotifications, 60 * 1000);

    return () => clearInterval(interval);
  }, [queriesList, notifiedHistory]);

  // This component now only manages notifications, no ToastContainer
  return null;
};

export default GlobalNotificationListener;